import type { RGB } from '@tma.js/sdk-react'
import { mainButton, secondaryButton } from '@tma.js/sdk-react'
import { useLayoutEffect, useRef, useSyncExternalStore } from 'react'
import { isDarkTheme, onThemeChange } from './theme.ts'

// MainButton/SecondaryButton Telegram как стек владельцев: экран кладёт свою кнопку, открытая шторка —
// свою поверх (priority), при закрытии шторки возвращается кнопка экрана.
// Вне Telegram те же кнопки рисуются на странице (FallbackBottomBar).

export type ButtonTone = 'default' | 'danger' | 'done'

export interface BottomButtonOptions {
  text: string
  onClick: () => void
  visible?: boolean
  loading?: boolean
  disabled?: boolean
  tone?: ButtonTone
  /** Чем выше, тем «главнее» владелец (шторки — 1). */
  priority?: number
}

type Slot = 'main' | 'secondary'

interface Entry {
  id: number
  priority: number
  text: string
  visible: boolean
  loading: boolean
  disabled: boolean
  tone: ButtonTone
  onClick: () => void
}

interface Snapshot {
  main: Entry | null
  secondary: Entry | null
}

const stacks: Record<Slot, Entry[]> = { main: [], secondary: [] }
const listeners = new Set<() => void>()
let snapshot: Snapshot = { main: null, secondary: null }
let seq = 0
let nativeMode = false

function topOf(slot: Slot): Entry | null {
  let best: Entry | null = null
  for (const e of stacks[slot]) {
    if (!best || e.priority > best.priority || (e.priority === best.priority && e.id > best.id)) best = e
  }
  return best
}

function emit(): void {
  snapshot = { main: topOf('main'), secondary: topOf('secondary') }
  if (nativeMode) syncNative()
  for (const l of listeners) l()
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function getSnapshot(): Snapshot {
  return snapshot
}

function upsert(slot: Slot, entry: Entry): void {
  const list = stacks[slot]
  const i = list.findIndex((e) => e.id === entry.id)
  if (i >= 0) list[i] = entry
  else list.push(entry)
  emit()
}

function remove(slot: Slot, id: number): void {
  stacks[slot] = stacks[slot].filter((e) => e.id !== id)
  emit()
}

// ───────── Нативные кнопки Telegram ─────────

interface Palette {
  primaryBg: RGB
  primaryFg: RGB
  secondaryBg: RGB
  secondaryFg: RGB
  disabledBg: RGB
  disabledFg: RGB
  danger: RGB
  done: RGB
}

const LIGHT: Palette = {
  primaryBg: '#2B2A2E',
  primaryFg: '#FFFFFF',
  secondaryBg: '#E6E5E9',
  secondaryFg: '#2B2A2E',
  disabledBg: '#E6E5E9',
  disabledFg: '#8E8D93',
  danger: '#C23F26',
  done: '#3FA772',
}

const DARK: Palette = {
  primaryBg: '#F3F2F5',
  primaryFg: '#2B2A2E',
  secondaryBg: '#3A393E',
  secondaryFg: '#F3F2F5',
  disabledBg: '#3A393E',
  disabledFg: '#8E8D93',
  danger: '#FF7A5C',
  done: '#3FA772',
}

function syncNative(): void {
  const p = isDarkTheme() ? DARK : LIGHT
  const m = snapshot.main
  try {
    if (m?.visible) {
      const off = m.disabled
      mainButton.setParams({
        text: m.text,
        isVisible: true,
        isEnabled: !off && !m.loading,
        isLoaderVisible: m.loading,
        hasShineEffect: false,
        bgColor: off
          ? p.disabledBg
          : m.tone === 'done'
            ? p.done
            : m.tone === 'danger'
              ? p.danger
              : p.primaryBg,
        textColor: off ? p.disabledFg : m.tone === 'default' ? p.primaryFg : '#FFFFFF',
      })
    } else {
      mainButton.setParams({ isVisible: false, isLoaderVisible: false })
    }
  } catch {
    // кнопка не смонтирована
  }
  try {
    if (!secondaryButton.setParams.isAvailable()) return
    const s = snapshot.secondary
    if (s?.visible) {
      secondaryButton.setParams({
        text: s.text,
        isVisible: true,
        isEnabled: !s.disabled && !s.loading,
        isLoaderVisible: s.loading,
        hasShineEffect: false,
        position: 'left',
        bgColor: p.secondaryBg,
        textColor: s.disabled ? p.disabledFg : s.tone === 'danger' ? p.danger : p.secondaryFg,
      })
    } else {
      secondaryButton.setParams({ isVisible: false, isLoaderVisible: false })
    }
  } catch {
    // нет поддержки SecondaryButton (Bot API < 7.10)
  }
}

function click(slot: Slot): void {
  const e = snapshot[slot]
  if (!e?.visible || e.disabled || e.loading) return
  e.onClick()
}

export function installNativeButtons(): void {
  nativeMode = true
  try {
    mainButton.onClick(() => click('main'))
  } catch {
    // нет MainButton
  }
  try {
    if (secondaryButton.onClick.isAvailable()) secondaryButton.onClick(() => click('secondary'))
  } catch {
    // нет SecondaryButton
  }
  onThemeChange(syncNative)
  syncNative()
}

export function isNativeButtons(): boolean {
  return nativeMode
}

// ───────── Хуки ─────────

function useBottomButton(slot: Slot, opts: BottomButtonOptions): void {
  const { text, visible = true, loading = false, disabled = false, tone = 'default', priority = 0 } = opts
  const handler = useRef(opts.onClick)
  const idRef = useRef(0)
  if (idRef.current === 0) {
    seq += 1
    idRef.current = seq
  }

  useLayoutEffect(() => {
    handler.current = opts.onClick
  })

  useLayoutEffect(() => {
    upsert(slot, {
      id: idRef.current,
      priority,
      text,
      visible,
      loading,
      disabled,
      tone,
      onClick: () => handler.current(),
    })
  }, [slot, priority, text, visible, loading, disabled, tone])

  useLayoutEffect(() => {
    const id = idRef.current
    return () => remove(slot, id)
  }, [slot])
}

/** Главная кнопка экрана (Telegram MainButton или кнопка внизу страницы). Снимается при размонтировании. */
export function useMainButton(opts: BottomButtonOptions): void {
  useBottomButton('main', opts)
}

/** Вторая кнопка (Telegram SecondaryButton слева от главной). */
export function useSecondaryButton(opts: BottomButtonOptions): void {
  useBottomButton('secondary', opts)
}

/** Скрыть кнопки экрана, пока открыт оверлей без своих действий. */
export function useHideBottomButtons(active: boolean): void {
  useMainButton({ text: '', onClick: noop, visible: false, priority: active ? 1 : -1 })
  useSecondaryButton({ text: '', onClick: noop, visible: false, priority: active ? 1 : -1 })
}

function noop(): void {}

// ───────── Запасная панель на странице ─────────

export function FallbackBottomBar() {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  const main = snap.main?.visible ? snap.main : null
  const secondary = snap.secondary?.visible ? snap.secondary : null
  const shown = !nativeMode && (main !== null || secondary !== null)

  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--ovo-bb-h', shown ? '64px' : '0px')
  }, [shown])

  if (!shown) return null
  return (
    <div className="bottom-bar">
      <div className="bottom-bar__inner">
        {secondary && (
          <button
            type="button"
            className={`btn ${secondary.tone === 'danger' ? 'btn--danger' : 'btn--secondary'}`}
            disabled={secondary.disabled || secondary.loading}
            onClick={() => click('secondary')}
          >
            {secondary.loading ? <span className="spinner" aria-hidden="true" /> : null}
            {secondary.text}
          </button>
        )}
        {main && (
          <button
            type="button"
            className={`btn ${main.tone === 'done' ? 'btn--done' : main.tone === 'danger' ? 'btn--danger' : 'btn--primary'}`}
            disabled={main.disabled || main.loading}
            onClick={() => click('main')}
          >
            {main.loading ? <span className="spinner" aria-hidden="true" /> : null}
            {main.text}
          </button>
        )}
      </div>
    </div>
  )
}
